import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useState, useContext, useEffect } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
  ToggleSwitch,
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import { useIdempotentCreate, fingerprintProperties, mutationInput } from "../../utils/idempotency";

const entityQuery = gql`
  query regionList($region_list_request: region_list_request) {
    region(region_list_request: $region_list_request) {
      edges {
        id
        name
        status
        sequence
      }
      totalCount
    }
  }
`;

const entityMutation = gql`
  mutation regionMutate($region_mutation_request: MutationInput) {
    region(region_mutation_request: $region_mutation_request) {
      id
      isNew
    }
  }
`;

export default function RegionEdit({ id: entityId, action, navigate }) {
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);

  let variables = {
    region_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
    },
  };

  const [name, setName] = useState("");
  const [sequence, setSequence] = useState();
  const [status, setStatus] = useState(isEdit ? 0 : 1);
  const [invalidReasons, setInvalidReasons] = useState([]);
  const createProperties = [
    { key: "name", string: name },
    { key: "sequence", string: sequence },
    { key: "status", number: status ? 1 : 0 },
  ];
  const { idempotencyKey, isSaving, trackSave } = useIdempotentCreate({
    enabled: !isEdit,
    fingerprint: fingerprintProperties(createProperties),
  });

  const {
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    skip: !isEdit,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const region = localData?.region?.edges?.[0];
      if (!region) return;
      setName(region.name);
      setSequence(region.sequence);
      setStatus(region.status);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const validateForm = () => {
    const reasons = [];
    if (!name || name.trim() === "") reasons.push("Name is required.");
    if (sequence === undefined || sequence === null || sequence === "") {
      reasons.push("Order (Sequence) is required.");
    }
    if (isNaN(Number(sequence))) reasons.push("Order (Sequence) must be a valid number.");
    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      region_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: createProperties,
        idempotencyKey,
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response) => {
      addNotification("good", "Region saved successfully");
      const id = response?.region?.id;
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
    },
  });

  useEffect(() => {
    if (isEdit) refetchEntity();
  }, [entityId, isEdit, refetchEntity]);

  const loadingMutation = mutateInfo.loading;

  return loadingEntity && isEdit ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          {isEdit ? "Edit" : "Add"} Region
        </span>
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Status:</label>
            <ToggleSwitch
              checked={status}
              onChange={() => setStatus(!status)}
              size={{
                width: 60,
                height: 32,
                knob: 24,
                padding: 4,
              }}
            />
          </div>
          <div className="edit-property">
            <label>Name *:</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="edit-property">
            <label>Order *:</label>
            <input value={sequence} onChange={(e) => setSequence(e.target.value)} />
          </div>
        </div>
        {invalidReasons.length > 0 && (
          <InvalidReasons reasons={invalidReasons} />
        )}
        <div className="edit-section">
          <SaveButton
            saving={!isEdit && isSaving}
            disabled={isEdit && loadingMutation}
            onClick={() => {
              if (!validateForm()) return;
              if (isEdit) mutateFunction();
              else trackSave(() => mutateFunction());
            }}
          />
        </div>
      </div>
    </>
  );
}
